import './index.css'
import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'

interface TourStep {
  desktopSelector: string
  mobileSelector: string
  title: string
  body: string
  placement: 'top' | 'bottom' | 'left' | 'right'
  mobilePlacement: 'top' | 'bottom' | 'left' | 'right'
}

const STEPS: TourStep[] = [
  {
    desktopSelector: '[data-tour="summary-cards"]',
    mobileSelector:  '[data-tour="mobile-hero"]',
    title: 'Resumo financeiro',
    body: 'Acompanhe receitas, despesas e saldo do mês em tempo real, tudo em um só lugar.',
    placement: 'bottom',
    mobilePlacement: 'bottom',
  },
  {
    desktopSelector: '[data-tour="sidebar-nav"]',
    mobileSelector:  '[data-tour="bottom-nav"]',
    title: 'Navegação',
    body: 'Use o menu para alternar entre visão geral, entradas, saídas, cartões e configurações.',
    placement: 'right',
    mobilePlacement: 'top',
  },
  {
    desktopSelector: '[data-tour="add-btn"]',
    mobileSelector:  '[data-tour="add-btn-mobile"]',
    title: 'Novo lançamento',
    body: 'Registre uma receita ou despesa rapidamente. Categorize, adicione notas e parcele no cartão.',
    placement: 'bottom',
    mobilePlacement: 'top',
  },
  {
    desktopSelector: '[data-tour="cards-panel"]',
    mobileSelector:  '[data-tour="cards-panel"]',
    title: 'Cartões de crédito',
    body: 'Cadastre seus cartões, acompanhe o limite disponível e veja cada fatura detalhada.',
    placement: 'top',
    mobilePlacement: 'top',
  },
  {
    desktopSelector: '[data-tour="plan-badge"]',
    mobileSelector:  '[data-tour="bottom-nav"]',
    title: 'Comece de graça, evolua quando quiser',
    body: 'No plano gratuito você já tem o essencial. Faça upgrade para Pro e desbloqueie histórico completo, relatórios e muito mais.',
    placement: 'top',
    mobilePlacement: 'top',
  },
]

const PAD = 8
const GAP = 14
const TOOLTIP_W = 280
const MARGIN = 12

interface SpotRect { top: number; left: number; width: number; height: number }

interface Props {
  onDone: () => void
}

export default function OnboardingTour({ onDone }: Props) {
  const [stepIndex,    setStepIndex]    = useState(0)
  const [spotRect,     setSpotRect]     = useState<SpotRect | null>(null)
  const [isAnimating,  setIsAnimating]  = useState(false)
  const [isMobile,     setIsMobile]     = useState(() => window.innerWidth < 768)
  const nextBtnRef = useRef<HTMLButtonElement>(null)

  // Track mobile breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const measure = useCallback(() => {
    const step = STEPS[stepIndex]
    const selector = isMobile ? step.mobileSelector : step.desktopSelector
    const el = document.querySelector(selector)
    if (!el) { setSpotRect(null); return }
    el.scrollIntoView({ behavior: 'instant', block: 'nearest' })
    requestAnimationFrame(() => {
      const r = el.getBoundingClientRect()
      setSpotRect({
        top:    r.top    - PAD,
        left:   r.left   - PAD,
        width:  r.width  + PAD * 2,
        height: r.height + PAD * 2,
      })
    })
  }, [stepIndex, isMobile])

  useLayoutEffect(() => { measure() }, [measure])

  useEffect(() => {
    window.addEventListener('resize', measure, { passive: true })
    window.addEventListener('scroll', measure, { passive: true, capture: true })
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, { capture: true })
    }
  }, [measure])

  // Focus next button when step becomes visible
  useEffect(() => {
    if (!isAnimating) {
      const t = setTimeout(() => nextBtnRef.current?.focus(), 80)
      return () => clearTimeout(t)
    }
  }, [stepIndex, isAnimating])

  // Escape key dismisses
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onDone() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onDone])

  function advance() {
    if (stepIndex >= STEPS.length - 1) { onDone(); return }
    setIsAnimating(true)
    setTimeout(() => {
      setStepIndex(i => i + 1)
      setIsAnimating(false)
    }, 180)
  }

  function skip() { onDone() }

  // Tooltip positioning
  const step = STEPS[stepIndex]
  const placement = isMobile ? step.mobilePlacement : step.placement

  // Mobile bottom anchor for nav-targeting steps
  const useMobileAnchor = isMobile && (
    step.mobileSelector === '[data-tour="bottom-nav"]' ||
    step.mobileSelector === '[data-tour="add-btn-mobile"]'
  )

  let tooltipStyle: React.CSSProperties = {}
  if (spotRect && !useMobileAnchor) {
    const APPROX_H = 160
    if (placement === 'bottom') {
      let left = spotRect.left + spotRect.width / 2 - TOOLTIP_W / 2
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - TOOLTIP_W - MARGIN))
      const top = Math.min(spotRect.top + spotRect.height + GAP, window.innerHeight - APPROX_H - MARGIN)
      tooltipStyle = { top, left }
    } else if (placement === 'top') {
      let left = spotRect.left + spotRect.width / 2 - TOOLTIP_W / 2
      left = Math.max(MARGIN, Math.min(left, window.innerWidth - TOOLTIP_W - MARGIN))
      const bottom = window.innerHeight - spotRect.top + GAP
      tooltipStyle = { bottom: Math.max(MARGIN, bottom), left }
    } else if (placement === 'right') {
      const left = Math.min(spotRect.left + spotRect.width + GAP, window.innerWidth - TOOLTIP_W - MARGIN)
      let top = spotRect.top + spotRect.height / 2 - APPROX_H / 2
      top = Math.max(MARGIN, Math.min(top, window.innerHeight - APPROX_H - MARGIN))
      tooltipStyle = { top, left }
    } else if (placement === 'left') {
      const right = window.innerWidth - spotRect.left + GAP
      let top = spotRect.top + spotRect.height / 2 - APPROX_H / 2
      top = Math.max(MARGIN, Math.min(top, window.innerHeight - APPROX_H - MARGIN))
      tooltipStyle = { top, right: Math.max(MARGIN, right) }
    }
  }

  const isLast = stepIndex === STEPS.length - 1

  return (
    <>
      <div className="bc-tour__backdrop" onClick={skip} aria-hidden="true" />

      {spotRect && (
        <div
          className={'bc-tour__spotlight' + (isAnimating ? ' is-hidden' : '')}
          style={{
            top:    spotRect.top,
            left:   spotRect.left,
            width:  spotRect.width,
            height: spotRect.height,
          }}
          aria-hidden="true"
        />
      )}

      {spotRect && (
        <div
          className={'bc-tour__tooltip' + (useMobileAnchor ? ' bc-tour__tooltip--bottom-anchor' : '')}
          data-placement={useMobileAnchor ? undefined : placement}
          style={useMobileAnchor ? undefined : tooltipStyle}
          role="dialog"
          aria-modal="true"
          aria-label="Tour do BagCash"
        >
          <div className="bc-tour__step-count">Passo {stepIndex + 1} de {STEPS.length}</div>
          <h2 className="bc-tour__title">{step.title}</h2>
          <p className="bc-tour__body">{step.body}</p>
          <div className="bc-tour__footer">
            <button className="bc-tour__btn-skip" onClick={skip}>Pular</button>
            <div className="bc-tour__dots" aria-hidden="true">
              {STEPS.map((_, i) => (
                <div key={i} className={'bc-tour__dot' + (i === stepIndex ? ' is-active' : '')} />
              ))}
            </div>
            <button
              ref={nextBtnRef}
              className="bc-btn bc-btn--primary bc-btn--sm"
              onClick={advance}
            >
              {isLast ? 'Concluir' : 'Próximo'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
